import { arktypeResolver } from '@hookform/resolvers/arktype';
import { SlotProps } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { type } from 'arktype';
import React from 'react';

import { useCreateGroupMutation, useGetGroupsQuery } from '@/api/v1';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { UserFormEmailGlobalError } from '@/components/molecules/UserFormEmailGlobalError';
import { GroupFormFields } from '@/components/organisms/GroupFormFields';
import departments from '@/data/departments.json';
import { Group } from '@/DTOs/groups';
import { useFormWithErrors } from '@/hooks/useFormWithErrors';
import { useTranslator } from '@/hooks/useTranslator';
import { GroupFormData, groupSchema } from '@/schemas/group';
import { validEmail } from '@/schemas/utils';
import { LabelAndValue } from '@/types/labelAndValue';

const userSchema = type({
  firstName: type('string>0').configure({ message: 'VALIDATION_REQUIRED' }),
  lastName: type('string>0').configure({ message: 'VALIDATION_REQUIRED' }),
  email: type('string>0').configure({ message: 'VALIDATION_REQUIRED' }).narrow(validEmail),
  department: type('string>0').configure({ message: 'VALIDATION_REQUIRED' }),
  group: type('string>0').configure({ message: 'VALIDATION_REQUIRED' }),
});

type UserFormData = typeof userSchema.infer;

const CUSTOM_ROLE_VALUE = 'CUSTOM' as const;

interface Props {
  readonly initialValues?: Partial<UserFormData>;
  readonly submitText: string;
  readonly cancelText?: string;
  readonly mode?: 'edit' | 'create';
  readonly loading?: boolean;
  readonly isEditingSelf?: boolean;
  readonly error?: 'user.conflict' | 'activeUser.conflict' | null;

  onSubmit(data: UserFormData): Promise<void>;
}

export const UserForm: React.FC<React.PropsWithChildren<Props>> = (
  props: React.PropsWithChildren<Props>,
): React.ReactElement => {
  const {
    children,
    error,
    submitText,
    cancelText,
    initialValues = emptyValues,
    mode = 'create',
    loading = true,
    isEditingSelf = false,
    onSubmit,
  } = props;
  const tr = useTranslator();
  const { data: groups = [] } = useGetGroupsQuery();
  const [createGroup, createGroupStatus] = useCreateGroupMutation();

  const customRoleForm = useFormWithErrors<GroupFormData>({
    resolver: arktypeResolver(groupSchema),
    defaultValues: {
      groupName: '',
      description: '',
      roles: [],
    },
  });

  const {
    handleSubmit,
    register,
    formState: { isSubmitting },
    watch,
  } = useFormWithErrors<UserFormData>({
    resolver: arktypeResolver(userSchema),
    defaultValues: {
      firstName: initialValues.firstName ?? '',
      lastName: initialValues.lastName ?? '',
      email: initialValues.email ?? '',
      department: initialValues.department ?? '',
      group: initialValues.group ?? '',
    },
  });

  const [email, group] = watch(['email', 'group']);
  const isCustomGroup = React.useMemo((): boolean => group === CUSTOM_ROLE_VALUE, [group]);

  const resolveGroupIdAndCreateIfNeeded = React.useCallback(async (): Promise<string> => {
    if (!isCustomGroup) {
      return group;
    }

    const isValid = await customRoleForm.trigger();
    if (!isValid) {
      const rolesError = customRoleForm.getFieldState('roles').error?.message;
      if (rolesError) {
        console.info(customRoleForm.getValues().roles);
        console.error(rolesError);
      }

      throw RoleValidationError;
    }

    const data = customRoleForm.getValues();
    const { error, data: createdGroup } = await createGroup(data);
    if (error) {
      console.error(error);
      throw error;
    }

    return createdGroup.groupId;
  }, [isCustomGroup, customRoleForm, createGroup, group]);

  const handleFormSubmit = React.useCallback(
    async (data: UserFormData): Promise<void> => {
      try {
        const groupId = await resolveGroupIdAndCreateIfNeeded();
        await onSubmit?.({ ...data, group: groupId });
      } catch (error: unknown) {
        if (error === RoleValidationError) {
          // Ignore this one
          return;
        }
        console.error('Form submission failed:', error);
        return;
      }
    },
    [resolveGroupIdAndCreateIfNeeded, onSubmit],
  );

  const readOnlySlotProps = React.useMemo(
    (): SlotProps<any, any, any> => ({
      htmlInput: {
        readOnly: mode === 'edit',
      },
    }),
    [mode],
  );

  const spinning = React.useMemo(
    (): boolean => isSubmitting || loading || createGroupStatus.status === QueryStatus.pending,
    [createGroupStatus.status, isSubmitting, loading],
  );

  const canEditEmail = spinning || mode === 'edit';

  return (
    <form autoComplete="off" onSubmit={handleSubmit(handleFormSubmit)}>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" gap={2}>
          <TextField
            defaultValue={initialValues.firstName}
            fullWidth={true}
            disabled={spinning}
            {...register({ name: 'firstName', label: tr('FIRST_NAME') })}
          />
          <TextField
            defaultValue={initialValues.lastName}
            fullWidth={true}
            disabled={spinning}
            {...register({ name: 'lastName', label: tr('LAST_NAME') })}
          />
        </Box>
        <TextField
          defaultValue={initialValues.email}
          fullWidth={true}
          slotProps={readOnlySlotProps}
          disabled={canEditEmail}
          {...register({ name: 'email', label: tr('EMAIL') })}
        />
        <UserFormEmailGlobalError email={email} error={error} />

        <TextField
          defaultValue={initialValues.department}
          select={true}
          fullWidth={true}
          disabled={spinning}
          {...register({ name: 'department', label: tr('DEPARTMENT') })}>
          {departments.map(
            (item: LabelAndValue): React.ReactElement => (
              <MenuItem key={item.value} value={item.value}>
                {tr(item.label)}
              </MenuItem>
            ),
          )}
        </TextField>
        <Box>
          <TextField
            defaultValue={initialValues.group}
            select={true}
            fullWidth={true}
            disabled={spinning || isEditingSelf}
            {...register({ name: 'group', label: tr('ROLE') })}>
            {groups.map(
              (group: Group): React.ReactElement => (
                <MenuItem key={group.groupId} value={group.groupId}>
                  <Typography variant="body2">
                    <Typography variant="inherit" component="span">
                      {tr(group.groupName)}
                    </Typography>
                    {group.isIntegratorGroup && (
                      <Typography component="span" variant="inherit" color="text.secondary">
                        {' '}
                        ({tr('CUSTOM')})
                      </Typography>
                    )}
                  </Typography>
                </MenuItem>
              ),
            )}
            <MenuItem key={CUSTOM_ROLE_VALUE} value={CUSTOM_ROLE_VALUE}>
              {tr('CUSTOM')}
            </MenuItem>
          </TextField>
        </Box>

        {isCustomGroup && (
          <Box sx={styles.customRoleForm}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2">{tr('CREATE_CUSTOM_ROLE_NAME')}</Typography>
              <GroupFormFields form={customRoleForm} loading={spinning} />
            </Box>
          </Box>
        )}

        {children}
      </Box>
      <Stack alignItems="center" mt={6} gap={3}>
        <LoadingButton
          type="submit"
          variant="contained"
          fullWidth
          loading={spinning}
          disabled={spinning}>
          {tr(submitText)}
        </LoadingButton>
        {cancelText && (
          <Button variant="text" color="error" disableRipple>
            {tr(cancelText)}
          </Button>
        )}
      </Stack>
    </form>
  );
};

const styles: Record<string, SystemStyleObject> = {
  customRoleForm: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
    borderRadius: 4,
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    color: 'text.primary',
    padding: 0,
    margin: 0,
  },
};

const emptyValues: UserFormData = {
  firstName: '',
  lastName: '',
  email: '',
  department: '',
  group: '',
};

const RoleValidationError = new Error('Role validation failed');
